import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import VoiceInput from "@/components/VoiceInput";

export default function TalentSearch() {
  const [sp] = useSearchParams();
  const [query, setQuery] = useState(sp.get("q") || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (qOverride) => {
    const raw = typeof qOverride === "string" ? qOverride : query;
    const q = (raw || "").trim();
    if (!q) return;
    setBusy(true);
    try {
      const { data } = await api.post("/ai/talent-match", { query: q });
      setResult(data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    const q = sp.get("q");
    if (q) { setQuery(q); run(q); }
    // eslint-disable-next-line
  }, [sp]);

  return (
    <div className="space-y-6" data-testid="talent-search-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500">Recruit</div>
        <h1 className="text-3xl font-bold mt-1">Talent Search</h1>
        <p className="text-neutral-600 mt-1">Semantic AI search across candidates, powered by Claude.</p>
      </div>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g., Senior React engineer with startup experience"
            className="pl-9 pr-12 py-6 text-base" data-testid="talent-query" onKeyDown={(e) => e.key === "Enter" && run()} />
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <VoiceInput onTranscript={(t) => setQuery(t)} testId="talent-voice-btn" />
          </div>
        </div>
        <Button className="btn-primary rounded-full px-6" onClick={run} disabled={busy} data-testid="talent-search-btn">
          <Sparkles className="w-4 h-4 mr-1" /> {busy ? "Searching…" : "Search with AI"}
        </Button>
      </div>

      {result && (
        <>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-2"><Sparkles className="w-4 h-4 text-pink" /><h3 className="font-semibold">AI Recommendation</h3></div>
              <p className="text-sm text-neutral-700 whitespace-pre-wrap" data-testid="talent-reasoning">{result.reasoning}</p>
            </CardContent>
          </Card>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {result.candidates.map(c => (
              <Link key={c.candidate_id} to={`/app/recruit/candidates/${c.candidate_id}`}>
                <Card className="card-lift border-0 shadow-sm">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-navy text-white grid place-items-center text-sm font-bold">{c.name.split(" ").map(x=>x[0]).join("")}</div>
                      <div>
                        <div className="font-semibold">{c.name}</div>
                        <div className="text-xs text-neutral-500">{c.email}</div>
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                      <Badge variant="outline">{c.stage}</Badge>
                      {c.ai_score != null && <Badge className="bg-pink text-white border-0">{c.ai_score}</Badge>}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
